import {useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'
import {LoaderCircle} from 'lucide-react'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {Button, Input, Label} from '@/shared/ui'
import {useAuthStore} from '../model/authStore'

const loginSchema = z.object({
  email: z.email('Некорректный email'),
  password: z.string().min(1, 'Введите пароль'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export function LoginForm() {
  const navigate = useNavigate()
  const login = useAuthStore(s => s.login)
  const isLoading = useAuthStore(s => s.isLoading)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {email: '', password: ''},
  })

  const onSubmit = form.handleSubmit(async data => {
    try {
      await login(data.email, data.password)
      navigate('/')
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Произошла ошибка. Попробуйте снова.'
      form.setError('root', {message})
    }
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-invalid={Boolean(form.formState.errors.email)}
          {...form.register('email')}
        />
        {form.formState.errors.email && (
          <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password">Пароль</Label>
          <button
            type="button"
            className="cursor-pointer text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
            onClick={() => navigate('/forgot-password')}
          >
            Забыли пароль?
          </button>
        </div>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          aria-invalid={Boolean(form.formState.errors.password)}
          {...form.register('password')}
        />
        {form.formState.errors.password && (
          <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
        )}
      </div>
      {form.formState.errors.root && (
        <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
      )}
      <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <LoaderCircle className="size-5 animate-spin" />
            Вход...
          </>
        ) : (
          'Войти'
        )}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        Нет аккаунта?{' '}
        <button
          type="button"
          className="cursor-pointer text-primary underline-offset-4 hover:underline"
          onClick={() => navigate('/register')}
        >
          Зарегистрироваться
        </button>
      </p>
    </form>
  )
}
